import store from '@Store';
import { generatePath } from 'react-router';
import history from '@AppRoutes/history';
import { paths } from '@AppRoutes/constants';
import { fetchSortingProperties } from '@Containers/SortingProperties/SortingPropertiesService';
import { FEATURES } from '@Constants/rx-ui/constants';
import Auth from '@Services/Auth';
import { updateDialog } from '@Containers/Dialogs/DialogsActions';
import NotifyDialogsService from '@Containers/Dialogs/NotifyDialogsService';
import ExportReopenService from '@Services/ExportReopenService';
import { getCurrentSettingsFromAppStorage } from '@Pages/Heatmap/HeatmapSettingsStorage';
import HeatmapService from '@Pages/Heatmap/HeatmapService';
import AppStorage from '@Services/AppStorage';
import Catalyst from '@Services/Catalyst';
import ExportResource from '@Services/ExportResource';
import isEqual from 'lodash/isEqual';
import isUndefined from 'lodash/isUndefined';
import last from 'lodash/last';
import assignIn from 'lodash/assignIn';
import each from 'lodash/each';
import some from 'lodash/some';
import map from 'lodash/map';
import filter from 'lodash/filter';
import get from 'lodash/get';
import find from 'lodash/find';
import cloneDeep from 'lodash/cloneDeep';
import { dpitems, citations } from '@Constants';
import { typeOfExportModal } from '@Containers/ExportModal/ExportModalConstants';
import { EXPORT_TEXTS } from './ExportConstants';

// TODOs:
// 1. Update logic to use promises instead of returning dataApi object and then watching/updating its properties...
// 2. Get rid of localData...

let params = null;
let template = null;
let localData = {};

let datapointsRequest;

const microsoftExcelNewLayout = 'Microsoft Excel New Layout';

const includeOnlyFirstPatentOptionName = 'firstPatent';

function openDailyExportLimitReachedModal() {
  return NotifyDialogsService.openModalWindow({
    title: EXPORT_TEXTS.dailyLimitReachedTitle,
    html: `Your daily export limit has been reached. Please refer to our <a class="els-btn els-btn-link els-btn-sm blue" target="_blank" rel="noopener noreferrer" href="https://www.elsevier.com/legal/elsevier-website-terms-and-conditions?dgcid=RN_AGCM_Sourced_300005028">Elsevier Website Terms & Conditions</a> regarding downloading and using the downloaded data.`,
    type: 'info',
    backdrop: 'static',
    isClosed: true,
    trackingOptions: {
      message: 'Your daily export limit has been reached. Please save your search and continue your export tomorrow.',
      component: 'Export',
    },
    confirmText: 'Ok',
  });
}

/**
 * @description Add the dummy option for headline inside the
 *   additional_options container.
 */
function addHeadlineOptions() {
  each(localData.formats, (format) => {
    const lastOption = last(format.additional_options);

    // Check if the EXTRA option is already added
    if (lastOption && !lastOption.dummy) {
      // If options headline is enabled, add an additional option, in order
      // to have a related input text as described in visual design.
      if (format.headline === true) {
        format.additional_options.push({
          checked: false,
          disabled: false,
          label: 'Include a description in the document',
          name: 'headline',
          dummy: true,
        });
      }
    }
  });
}

function addIncludeOnlyFirstPatentOption() {
  if (params?.parentParams?.context !== citations) {
    return;
  }

  each(localData?.formats, (format) => {
    const includeOnlyFirstPatentOption = find(format?.additional_options, ({ name }) => name === includeOnlyFirstPatentOptionName);
    if (!includeOnlyFirstPatentOption) {
      format?.additional_options?.push({
        name: includeOnlyFirstPatentOptionName,
        label: 'Include only first patent of a family',
        checked: false,
        disabled: false,
        hidden: false,
      });
    }
  });
}

/**
 * @description Get data range options.
 */
function getRanges() {
  // RXFE-5423: these ranges must not be shown for dpitems and tgitems context
  // RX-9317: ranges are available for tgitems
  const additionalRanges = [
    {
      label: 'Selected - ',
      value: get(params, 'entries.selected.length'),
    },
    {
      label: 'Range (e.g. 1-2, 1-10)',
      value: '',
      max: params.entries.total,
    },
  ];

  if (isUndefined(localData.ranges)) {
    localData.ranges = [
      {
        label: 'All results - ',
        value: params.entries.total,
      },
    ];

    if (params.parentParams.context !== dpitems) {
      localData.ranges = localData.ranges.concat(additionalRanges);
    }
  } else {
    // Update "Selected" single property
    // eslint-disable-next-line no-lonely-if
    if (localData.ranges && localData.ranges.length > 1) {
      localData.ranges[1].value = get(params, 'entries.selected.length');
    }
  }

  return localData.ranges;
}

/**
 * @description Filter export_options and additional_options for display
 *   property in each option item.
 */
function filterDisplayOptions(nameProp) {
  const filtered = [];

  each(localData[nameProp], (item) => {
    if (item.display !== 'none') {
      filtered.push(item);
    }
  });

  return filtered;
}

/**
 * @description Get data export options.
 */
function getExports() {
  return filterDisplayOptions('exports');
}

/**
 * @description Retrieve data only if the container is undefined, otherwise
 *   return localData var.
 */
function getOptions() {
  return filterDisplayOptions('options');
}

function getExportSizeLimit() {
  return localData.exportSizeLimit;
}

const dataApi = {
  addHeadlineOptions,
  addIncludeOnlyFirstPatentOption,

  get formats() {
    return localData.formats;
  },
  set formats(formats) {
    localData.formats = formats;
  },
  get formatsTooltip() {
    return localData.formatsTooltip;
  },
  get ranges() {
    return getRanges();
  },
  get exports() {
    return getExports();
  },
  set exports(exports) {
    localData.exports = exports;
  },
  get options() {
    return getOptions();
  },
  set options(options) {
    localData.options = options;
  },
  get exportSizeLimit() {
    return getExportSizeLimit();
  },
  get exportSizeTooltip() {
    return localData.exportSizeTooltip;
  },
  get exportAttemptsCurrentlyUsed() {
    return localData.exportAttemptsCurrentlyUsed;
  },
  get exportAttemptsLimit() {
    return localData.exportAttemptsLimit;
  },
};

/* --------- API ---------*/

/**
 * @description Set/Reset the initial values for properties of localData.
 */
function setInitialData() {
  return {
    formats: localData.formats,
    formatsTooltip: localData.formatsTooltip,
    ranges: undefined,
    exports: undefined,
    options: undefined,
    exportSizeLimit: localData.exportSizeLimit,
    exportSizeTooltip: localData.exportSizeTooltip,
    exportAttemptsCurrentlyUsed: localData.exportAttemptsCurrentlyUsed,
    exportAttemptsLimit: localData.exportAttemptsLimit,
  };
}

/**
 * @description gets sorting param from relative context and sets it to current export payload
 */
async function overrideSorting(context, parentParams) {
  try {
    const sorting = await fetchSortingProperties(parentParams.relativeContexts[context]);
    const defaultOption = sorting.find((option) => option.default) || sorting[0];
    assignIn(parentParams, {
      sort: defaultOption.value,
      order: defaultOption.order,
    });
  } catch (error) {
    Logger.error(error);
  }
}

/**
 * @param {object} options Containing object with properties: parentParams,
 *   entries.
 * @returns {object} Current api methods and data.
 * @description Populate data object for modal form options and return api.
 */
function initializeData(options) {
  if (!options) {
    Logger.warn("Can't initialize the ExportService without options param.");

    return dataApi;
  }

  // Check if params/options are changed and reset the localData.
  if (params && !isEqual(params, options)) {
    localData = setInitialData();
  }

  // Set the local params var
  params = options;

  return dataApi;
}

/**
 * @description Always retrieve data since this request is being tracked on back end
 */
function getFormats(parentParams) {
  return ExportResource.getExportFormats(parentParams.databaseId, parentParams.context)
    .then((response) => {
      // Update the template name
      template = response.value;

      if (parentParams.context !== response.sortContext) {
        overrideSorting(response.sortContext, parentParams);
      }

      const exportSizeTooltip =
        // eslint-disable-next-line no-template-curly-in-string
        response.exportSizeTooltip && response.exportSizeTooltip.replace('${exportSizeLimit}', response.exportSizeLimit);

      localData.exportSizeLimit = response.exportSizeLimit;
      localData.exportSizeTooltip = exportSizeTooltip;
      localData.formats = response.formats;
      if (
        !Auth.isFeatureEnabled(FEATURES.disableMicrosoftExcelNewLayoutInHeatMapExportModal) &&
        parentParams.context === 'dpitems' &&
        response.formats.length > 0
      ) {
        const msExcelObj = find(response.formats, (item) => item.value === 'MSEXCEL');
        if (msExcelObj) {
          localData.formats.unshift(cloneDeep(msExcelObj));
          localData.formats[0].label = microsoftExcelNewLayout;
        }
      }
      localData.formatsTooltip = response.formatsTooltip;
      localData.exportAttemptsCurrentlyUsed = response.exportAttemptsCurrentlyUsed + 1;
      localData.exportAttemptsLimit = response.exportAttemptsLimit;

      return {
        formats: localData.formats,
        exportAttemptsCurrentlyUsed: localData.exportAttemptsCurrentlyUsed,
        exportAttemptsLimit: localData.exportAttemptsLimit,
      };
    })
    .catch(Logger.reject);
}

/**
 * @description Check if format exist
 */
function isFormatsExist() {
  return !!(localData && localData.formats);
}

/**
 * @description Get datapoints data
 */
function getDatapoints() {
  if (localData.datapoints) {
    return Promise.resolve(localData.datapoints);
  }

  if (!datapointsRequest) {
    const payload = {
      databaseId: params.parentParams.databaseId,
      context: params.parentParams.context,
      resultName: params.parentParams.resultName,
    };

    datapointsRequest = ExportResource.getDatapoints(payload)
      .then((response) => {
        datapointsRequest = null;
        // eslint-disable-next-line no-return-assign,sonarjs/no-nested-assignment
        return (localData.datapoints = response);
      })
      .catch(Logger.reject);
  }

  return datapointsRequest;
}

function isAnonExportNotAllowed() {
  const enableAnonymousExports = get(Auth.getUser(), 'details.enableAnonymousExports');
  return Auth.isAnonymousUser() && !enableAnonymousExports;
}

function checkAvailableFormats(data) {
  const types = [...data];
  return some(types, (type) => !type.disabled);
}

function getExportSignInTrackingName(parentParams, trackingName) {
  if (trackingName) return trackingName;

  const mappedContext = Catalyst.mapContext(parentParams?.context);
  return mappedContext ? `${mappedContext}:export:sign-in` : 'export:sign-in';
}

function openLoginDialog({ exportType, parentParams = {}, trackingName, postLoginRedirect }) {
  return NotifyDialogsService.openModalWindow({
    title: 'Login required',
    text: 'Please sign-in/register to be able to Export data.',
    cancelText: 'Cancel',
    confirmText: 'Sign in',
    type: 'info',
    onConfirm: () => {
      Catalyst.trackConversionDriverClick(getExportSignInTrackingName(parentParams, trackingName));
      if (postLoginRedirect) {
        AppStorage.instance.set('postLoginRedirect', postLoginRedirect);
      } else {
        AppStorage.instance.set('revisitLastState', true);
      }
      if (exportType) {
        ExportReopenService.saveReopenExport(exportType);
      }
      history.push({
        pathname: generatePath(paths.LOGIN),
      });
    },
    trackingOptions: {
      component: 'Export',
    },
  });
}

function openUnavailableFormatsInfo() {
  const errorTitle = 'Your export functionality has been disabled.';
  const errorMessage = 'Please contact customer care to inquire about re-enabling the export functionality.';
  return NotifyDialogsService.openModalWindow({
    title: errorTitle,
    text: errorMessage,
    confirmText: 'OK',
    type: 'info',
    backdrop: 'static',
    trackingOptions: {
      message: `${errorTitle} ${errorMessage}`,
      component: 'Export',
    },
  });
}

/**
 * @param {object} formData Options selected by the user.
 * @description Get the range of indexes selected by the user.
 */
function getRangeInserted(formData) {
  let range = '';

  switch (formData.range.selected) {
    case 1:
      range = map(params.entries.selected, 'index').join(',');
      break;

    case 2:
      range = `${formData.range.from}-${formData.range.to}`;
      break;

    default:
      // Reason for 1-1000: XML server doesn't accept higher range of 1000
      range = `1-${params.entries.total > localData.exportSizeLimit ? localData.exportSizeLimit : params.entries.total}`;
      break;
  }

  return range;
}

/**
 * @param {object} formData Options selected by the user.
 * @returns {array} An array with all options selected.
 * @description Get all export and additional options selected by the user.
 */
function getAllOptionsSelected(formData) {
  const allOptionsNames = [];
  const additionalOptions = filter(localData.options, { checked: true });

  each(additionalOptions, (option) => {
    if (!option.dummy && option.name) {
      allOptionsNames.push(option.name);
    }
  });

  if (formData.export) {
    allOptionsNames.push(formData.export);
  }

  return allOptionsNames;
}

/**
 * @param {object} formData Form user data preferences object.
 * @returns {object} The object ready to be used as payload.
 * @description Prepare the payload for results.
 */
function prepareResultsExportPayload(formData) {
  const { rowsMax, colsMax, matrixName } = HeatmapService.getMatrixRowAndCol();
  const payload = {
    databaseId: Number(params.parentParams.databaseId),
    resultName: params.parentParams.resultName,
    docFormat: formData.format.value,
    range: getRangeInserted(formData),
    options: getAllOptionsSelected(formData),
    template,
    headline: formData.options.headline,
    items: formData.items, // Optional
    sortField: params.parentParams.sort,
    sortOrder: params.parentParams.order || 'asc',
    rowsMax,
    colsMax,
    matrixName: params.parentParams.origSortedMatrixName || matrixName || null,
    hitSetKey: params.parentParams.hitSetKey,
    resultSize: formData.resultSize,
  };
  const settings = getCurrentSettingsFromAppStorage();
  if (!Auth.isFeatureEnabled(FEATURES.disableMicrosoftExcelNewLayoutInHeatMapExportModal) && params.parentParams.context === 'dpitems') {
    payload.options.push(`heatmap:true`);
    if (formData.format.placeholder === microsoftExcelNewLayout) {
      payload.options.push(`layout:MSEXCEL_HEATMAP`);
      payload.options.push(`X-axis:${get(settings, 'row.value', '')}`);
      payload.options.push(`Y-axis:${get(settings, 'col.value', '')}`);
    }
  }
  if (payload.docFormat === 'RefWorks') {
    payload.options.push('citNeedFrontpage');
  }
  return payload;
}

/**
 * @param {object} formData Form user data preferences object.
 * @returns {object} The object ready to be used as payload.
 * @description Prepare the payload for ChemQuote Export.
 */
function prepareChemQuoteExportPayload(formData) {
  const payload = {
    databaseId: Number(params.parentParams.databaseId),
    resultName: params.parentParams.resultName,
    sortField: params.parentParams.sort,
    sortOrder: params.parentParams.order || 'asc',
    hitSetKey: params.parentParams.hitSetKey,
    resultSize: formData.resultSize,
  };

  if (get(formData, 'range.selected') === 1) {
    // Use ids if items manually selected
    assignIn(payload, {
      ids: map(params.entries.selected, 'id'),
    });
  } else {
    // Use range otherwise
    assignIn(payload, {
      range: getRangeInserted(formData),
    });
  }

  return payload;
}

export const openExportModal = (exportType, parentParams, entries) => {
  store.dispatch(
    updateDialog({
      id: typeOfExportModal,
      dialogType: typeOfExportModal,
      movable: false,
      backdrop: true,
      exportType,
      parentParams,
      entries,
    })
  );
};

/**
 * @param {object} resolve Pass a resolve object from coming controller
 * @description Open dialog window for export action.
 * */
function openResultExportDialog({ exportType, parentParams, entries, noFormats }) {
  if (isAnonExportNotAllowed()) {
    return openLoginDialog({ exportType, parentParams });
  }

  let promise;

  // eslint-disable-next-line prefer-const
  promise = getFormats(parentParams)
    .then(({ formats, exportAttemptsCurrentlyUsed, exportAttemptsLimit }) => {
      if (!noFormats && !checkAvailableFormats(formats)) {
        return openUnavailableFormatsInfo();
      }

      if (exportAttemptsCurrentlyUsed > exportAttemptsLimit) {
        return openDailyExportLimitReachedModal();
      }

      return openExportModal(exportType, parentParams, entries);
    })
    .catch(Logger.reject);

  return promise;
}

export {
  initializeData,
  getDatapoints,
  isFormatsExist,
  openResultExportDialog,
  prepareResultsExportPayload,
  prepareChemQuoteExportPayload,
  getFormats,
  openLoginDialog,
  openDailyExportLimitReachedModal,
  microsoftExcelNewLayout,
  includeOnlyFirstPatentOptionName,
};
